<template>
    <div class="home-container">
        <!-- Hero Section -->
        <section class="hero">
            <div class="hero-content">
                <p class="hero-subtitle">Research Intern at National Institute of Informatics, Tokyo</p>
                <div class="hero-badges">
                    <span class="badge">Computer Science Master's</span>
                    <span class="badge">IST Lisbon</span>
                </div>
            </div>
        </section>

        <!-- Main Content -->
        <div class="content-wrapper">
            <!-- About Section -->
            <section id="about" class="section">
                <div class="section-header">
                    <h2 class="section-title">About Me</h2>
                    <div class="section-line"></div>
                </div>
                <div class="card">
                    <div class="about-content">
                        <!-- Profile Photo -->
                        <div class="profile-photo-container">
                            <div class="profile-photo">
                                <img 
                                    src="../assets/images/photo.jpg" 
                                    alt="Sebastião Carvalho - Profile Photo"
                                    class="profile-image"
                                    @error="handleImageError"
                                />
                                <!-- Fallback placeholder when image is not available -->
                                <div class="profile-placeholder" v-if="imageError">
                                    <div class="placeholder-icon">👤</div>
                                    <span class="placeholder-text">Profile Photo</span>
                                </div>
                            </div>
                            <div class="photo-glow"></div>
                        </div>
                        
                        <!-- Text Content -->
                        <div class="text-content-wrapper">
                            <p class="text-content">
                                My name is Sebastião Carvalho, I am {{ age }} years old and I am a big enthusiast in the areas of Formal Methods,
                                Artificial Intelligence, and VideoGames. I am also a big fan of comic book adaptations and horror movies.
                                I am a Computer Science Master's student at Instituto Superior Técnico, Lisbon, Portugal. I plan to specialize in Artificial Intelligence
                                and VideoGames.
                            </p>
                            <p class="text-content">
                                If you wish to learn more about my professional experience check the <router-link to="/career" class="link">Career</router-link> page.
                                If you wish to learn more about the games I developed check the <router-link to="/projects" class="link">Projects</router-link> page.
                                If you wish to learn more about my academic background check the <router-link to="/academics" class="link">Academics</router-link> page.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <!-- Skills Section -->
            <section id="skills" class="section">
                <div class="section-header">
                    <h2 class="section-title">Skills</h2>
                    <div class="section-line"></div>
                </div>
                <div class="skills-grid">
                    <div class="skill-card">
                        <div class="skill-icon">💻</div>
                        <h3 class="skill-title">Programming Languages</h3>
                        <div class="skill-tags">
                            <span class="skill-tag">C++</span>
                            <span class="skill-tag">Java</span>
                            <span class="skill-tag">JavaScript</span>
                            <span class="skill-tag">Python</span>
                            <span class="skill-tag">Dafny</span>
                            <span class="skill-tag">Alloy</span>
                            <span class="skill-tag">C#</span>
                        </div>
                    </div>
                    <div class="skill-card">
                        <div class="skill-icon">🚀</div>
                        <h3 class="skill-title">Frameworks</h3>
                        <div class="skill-tags">
                            <span class="skill-tag">Maven</span>
                            <span class="skill-tag">Spring</span>
                            <span class="skill-tag">Unreal 5</span>
                            <span class="skill-tag">Unity</span>
                            <span class="skill-tag">Godot</span>
                            <span class="skill-tag">Rodin Event-B</span>
                        </div>
                    </div>
                    <div class="skill-card">
                        <div class="skill-icon">🛠️</div>
                        <h3 class="skill-title">Tools</h3>
                        <div class="skill-tags">
                            <span class="skill-tag">Git & GitHub/GitLab</span>
                            <span class="skill-tag">OpenAI API</span>
                        </div>
                    </div>
                </div>
            </section>

            <!-- Contact Section -->
            <section id="contacts" class="section">
                <div class="section-header">
                    <h2 class="section-title">Contacts</h2>
                    <div class="section-line"></div>
                </div>
                
                <div class="contacts-grid">
                    <!-- Contact Cards -->
                    <div class="contact-card">
                        <div class="contact-icon">📧</div>
                        <h3 class="contact-title">Email</h3>
                        <a href="mailto:sebastiaovscarvalho@gmail.com" class="contact-link">
                            sebastiaovscarvalho@gmail.com
                        </a>
                    </div>
                    
                    <div class="contact-card">
                        <div class="contact-icon">💼</div>
                        <h3 class="contact-title">LinkedIn</h3>
                        <a href="https://www.linkedin.com/in/sebasti%C3%A3o-carvalho-6036a026b/" target="_blank" rel="noopener noreferrer" class="contact-link">
                            linkedin.com/in/sebastião-carvalho-6036a026b/
                        </a>
                    </div>

                    <div class="contact-card">
                        <div class="contact-icon">🏢</div>
                        <h3 class="contact-title">Institution</h3>
                        <a href="https://www.nii.ac.jp/en/" target="_blank" rel="noopener noreferrer" class="contact-link">
                            National Institute of Informatics
                        </a>
                    </div>
                </div>
            </section>
        </div>
    </div>
</template>

<script>
export default {
    name: 'HomeView',
    data() {
        return {
            imageError: false
        }
    },
    computed: {
        age() {
            const birthDate = new Date('2002-09-06');
            const today = new Date();
            let age = today.getFullYear() - birthDate.getFullYear();
            
            // Check if birthday hasn't occurred this year yet
            const monthDiff = today.getMonth() - birthDate.getMonth();
            if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                age--;
            }
            return age;
        }
    },
    methods: {
        handleImageError() {
            this.imageError = true;
        }
    }
}
</script>

<style lang="scss" scoped>
// Using the same CSS variables as the main app
:root {
    --primary-color: #00d4ff;
    --primary-gradient: linear-gradient(135deg, #00d4ff 0%, #5b73ff 100%);
    --accent-color: #ff6b6b;
    --accent-gradient: linear-gradient(135deg, #ff6b6b 0%, #ffa500 100%);
    --secondary-color: #1a1d23;
    --background-dark: #0f1419;
    --background-darker: #0a0e13;
    --background-card: #1a1d23;
    --text-light: #e6e6e6;
    --text-bright: #ffffff;
    --text-muted: #8892b0;
    --text-dim: #64748b;
    --border-color: #2d3748;
    --border-glow: rgba(0, 212, 255, 0.2);
    --shadow-dark: 0 4px 20px rgba(0, 0, 0, 0.4);
    --shadow-hover: 0 8px 30px rgba(0, 0, 0, 0.6);
    --shadow-glow: 0 0 20px rgba(0, 212, 255, 0.1);
}

.home-container {
    min-height: 100vh;
    color: var(--text-light);
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    line-height: 1.6;
}

// Hero Section (Simplified since title is in App.vue)
.hero {
    text-align: center;
    padding: 2rem 0;
    margin-bottom: 2rem;
}

.hero-content {
    max-width: 800px;
    margin: 0 auto;
    padding: 0 2rem;
}

.hero-subtitle {
    font-size: 1.5rem;
    font-weight: 300;
    margin-bottom: 2rem;
    color: var(--text-muted);
    animation: fadeInUp 1s ease-out;
}

.hero-badges {
    display: flex;
    gap: 1rem;
    justify-content: center;
    flex-wrap: wrap;
    animation: fadeInUp 1s ease-out 0.2s both;
}

.badge {
    background: linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(91, 115, 255, 0.1) 100%);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(0, 212, 255, 0.3);
    padding: 0.5rem 1rem;
    border-radius: 25px;
    font-size: 0.9rem;
    font-weight: 500;
    color: var(--text-bright);
    transition: all 0.3s ease;
    
    &:hover {
        background: linear-gradient(135deg, rgba(0, 212, 255, 0.2) 0%, rgba(91, 115, 255, 0.2) 100%);
        transform: translateY(-2px);
        box-shadow: 0 4px 15px rgba(0, 212, 255, 0.3);
    }
}

// Content Wrapper
.content-wrapper {
    max-width: 1200px;
    margin: 0 auto;
    padding: 2rem;
}

// Section Styles
.section {
    margin-bottom: 4rem;
    animation: fadeInUp 0.8s ease-out;
}

.section-header {
    text-align: center;
    margin-bottom: 3rem;
}

.section-title {
    font-size: 2.5rem;
    font-weight: 700;
    color: var(--primary-color);
    margin-bottom: 1rem;
    position: relative;
    display: inline-block;
}

.section-line {
    width: 60px;
    height: 4px;
    background: var(--primary-gradient);
    margin: 0 auto;
    border-radius: 2px;
}

// Dark Theme Card Styles
.card {
    background: var(--background-card);
    border-radius: 16px;
    padding: 2.5rem;
    box-shadow: var(--shadow-dark), var(--shadow-glow);
    transition: all 0.3s ease;
    border: 1px solid var(--border-color);
    
    &:hover {
        box-shadow: var(--shadow-hover), 0 0 30px rgba(0, 212, 255, 0.15);
        transform: translateY(-4px);
        border-color: var(--border-glow);
    }
}

// About Section with Photo
.about-content {
    display: grid;
    grid-template-columns: 280px 1fr;
    gap: 3rem;
    align-items: start;
    
    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 2rem;
        text-align: center;
    }
}

.profile-photo-container {
    position: relative;
    display: flex;
    justify-content: center;
    
    @media (max-width: 768px) {
        margin-bottom: 1rem;
    }
}

.profile-photo {
    position: relative;
    width: 240px;
    height: 300px;
    border-radius: 16px;
    overflow: hidden;
    border: 4px solid transparent;
    background: var(--primary-gradient);
    padding: 4px;
    transition: all 0.3s ease;
    
    &:hover {
        transform: scale(1.05);
        
        .photo-glow {
            opacity: 1;
            transform: scale(1.1);
        }
    }
    
    @media (max-width: 480px) {
        width: 200px;
        height: 250px;
    }
}

.profile-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 12px;
    background: var(--background-darker);
    transition: all 0.3s ease;
}

.profile-placeholder {
    width: 100%;
    height: 100%;
    background: var(--background-darker);
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    
    .placeholder-icon {
        font-size: 4rem;
        color: var(--text-dim);
    }
    
    .placeholder-text {
        font-size: 0.9rem;
        color: var(--text-dim);
        font-weight: 500;
    }
}

.photo-glow {
    position: absolute;
    top: -10px;
    left: -10px;
    right: -10px;
    bottom: -10px;
    background: var(--primary-gradient);
    border-radius: 20px;
    opacity: 0;
    z-index: -1;
    filter: blur(20px);
    transition: all 0.3s ease;
}

.text-content-wrapper {
    .text-content {
        font-size: 1.1rem;
        line-height: 1.8;
        margin-bottom: 1.5rem;
        color: var(--text-light);
        
        &:last-child {
            margin-bottom: 0;
        }
    }
}

// Links with Dark Theme
.link {
    color: var(--primary-color);
    text-decoration: none;
    font-weight: 600;
    position: relative;
    transition: all 0.3s ease;
    
    &:hover {
        color: #5b73ff;
        
        &::after {
            transform: scaleX(1);
        }
    }
    
    &::after {
        content: '';
        position: absolute;
        bottom: -2px;
        left: 0;
        right: 0;
        height: 2px;
        background: var(--primary-gradient);
        transform: scaleX(0);
        transition: transform 0.3s ease;
    }
}

// Skills Grid
.skills-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
    gap: 2rem;
}

.skill-card {
    background: var(--background-card);
    border-radius: 16px;
    padding: 2rem;
    box-shadow: var(--shadow-dark), var(--shadow-glow);
    transition: all 0.3s ease;
    border: 1px solid var(--border-color);
    text-align: center;
    
    &:hover {
        box-shadow: var(--shadow-hover), 0 0 30px rgba(0, 212, 255, 0.15);
        transform: translateY(-4px);
        border-color: var(--border-glow);
    }
}

.skill-icon {
    font-size: 2.5rem;
    margin-bottom: 1rem;
    display: block;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
}

.skill-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: var(--text-bright);
    margin-bottom: 1.5rem;
}

.skill-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    justify-content: center;
}

.skill-tag {
    background: var(--primary-gradient);
    color: white;
    padding: 0.4rem 0.8rem;
    border-radius: 20px;
    font-size: 0.85rem;
    font-weight: 500;
    transition: all 0.3s ease;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    
    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 15px rgba(0, 212, 255, 0.4);
    }
}

// Animations
@keyframes fadeInUp {
    from {
        opacity: 0;
        transform: translateY(30px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

// Responsive Design
@media (max-width: 768px) {
    .hero-subtitle {
        font-size: 1.2rem;
    }
    
    .content-wrapper {
        padding: 1.5rem;
    }
    
    .card {
        padding: 1.5rem;
    }
    
    .section-title {
        font-size: 2rem;
    }
    
    .skills-grid {
        grid-template-columns: 1fr;
    }
    
    .skill-card {
        padding: 1.5rem;
    }
    .contacts-grid {
        grid-template-columns: 1fr;
        gap: 1rem;
    }
    
    .contact-card {
        padding: 1.5rem;
    }
    
    .contact-icon {
        font-size: 2rem;
    }
    
    .contact-title {
        font-size: 1.1rem;
    }
}

@media (max-width: 480px) {
    .hero-subtitle {
        font-size: 1rem;
    }
    
    .hero-badges {
        flex-direction: column;
        align-items: center;
    }
    
    .content-wrapper {
        padding: 1rem;
    }
    
    .card {
        padding: 1rem;
    }
    
    .section-title {
        font-size: 1.5rem;
    }
    .contact-card {
        padding: 1rem;
    }
    
    .contact-form-title {
        font-size: 1.5rem;
    }
}

.contacts-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1.5rem;
    margin-bottom: 4rem;
}

.contact-card {
    background: var(--background-card);
    border-radius: 16px;
    padding: 2rem;
    box-shadow: var(--shadow-dark), var(--shadow-glow);
    transition: all 0.3s ease;
    border: 1px solid var(--border-color);
    text-align: center;
    
    &:hover {
        box-shadow: var(--shadow-hover), 0 0 30px rgba(0, 212, 255, 0.15);
        transform: translateY(-4px);
        border-color: var(--border-glow);
    }
}

.contact-icon {
    font-size: 2.5rem;
    margin-bottom: 1rem;
    display: block;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
}

.contact-title {
    font-size: 1.3rem;
    font-weight: 600;
    color: var(--text-bright);
    margin-bottom: 1rem;
}

.contact-link {
    color: var(--primary-color);
    text-decoration: none;
    font-weight: 500;
    font-size: 0.95rem;
    position: relative;
    transition: all 0.3s ease;
    display: inline-block;
    
    &:hover {
        color: #5b73ff;
        transform: translateY(-2px);
        
        &::after {
            transform: scaleX(1);
        }
    }
    
    &::after {
        content: '';
        position: absolute;
        bottom: -2px;
        left: 0;
        right: 0;
        height: 2px;
        background: var(--primary-gradient);
        transform: scaleX(0);
        transition: transform 0.3s ease;
    }
}

// Contact Form Styles
.contact-form-wrapper {
    max-width: 800px;
    margin: 0 auto;
}

.contact-form-title {
    font-size: 1.8rem;
    font-weight: 600;
    color: var(--text-bright);
    text-align: center;
    margin-bottom: 2rem;
}

.contact-form {
    .form-row {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 1.5rem;
        margin-bottom: 1.5rem;
        
        @media (max-width: 768px) {
            grid-template-columns: 1fr;
            gap: 1rem;
        }
    }
    
    .form-group {
        margin-bottom: 1.5rem;
    }
    
    .form-label {
        display: block;
        font-weight: 500;
        color: var(--text-bright);
        margin-bottom: 0.5rem;
        font-size: 0.95rem;
    }
    
    .form-input,
    .form-textarea {
        width: 100%;
        padding: 0.75rem 1rem;
        background: var(--background-darker);
        border: 2px solid var(--border-color);
        border-radius: 8px;
        color: var(--text-light);
        font-size: 0.95rem;
        transition: all 0.3s ease;
        font-family: inherit;
        
        &:focus {
            outline: none;
            border-color: var(--primary-color);
            box-shadow: 0 0 0 3px rgba(0, 212, 255, 0.1);
        }
        
        &::placeholder {
            color: var(--text-dim);
        }
    }
    
    .form-textarea {
        resize: vertical;
        min-height: 120px;
    }
}

.contact-submit-btn {
    background: var(--primary-gradient);
    border: none;
    padding: 1rem 2rem;
    border-radius: 12px;
    color: white;
    font-weight: 600;
    font-size: 1rem;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    margin: 2rem auto 0;
    box-shadow: 0 4px 15px rgba(0, 212, 255, 0.3);
    
    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(0, 212, 255, 0.4);
    }
    
    &:active {
        transform: translateY(0);
    }
    
    .btn-icon {
        font-size: 1.1rem;
    }
}
</style>